<template>
    <div class="send-options">
        <template v-if="!pkg.is_public">
            <label v-if="hasContactPin">
                <i class="far fa-lock"></i>
                This contact has PIN protection enabled
            </label>
            <div v-else>
                <label>
                    <i v-if="org.settings.requirePin" class="far fa-lock"></i>
                    <input v-else type="checkbox" v-model="options.enablePin" />
                    Protect this form with a PIN
                </label>
                <collapse-section :show="options.enablePin">
                    <form-input v-model="options.pin" :trim="true" label="Enter a PIN" ref="pin" paddingTop="1em" />
                    <form-input v-model="options.pinHint" :trim="true" label="Add a hint for this PIN (optional)" />
                </collapse-section>
            </div>
        </template>
        <div>
            <label><input type="checkbox" v-model="options.disablePrint" /> Disable print/download</label>
            <i
                class="far fa-info-circle"
                v-tooltip="'Hides the print and download buttons from the filler until after submission'"
            ></i>
        </div>
        <div>
            <label><input type="checkbox" v-model="options.anonymous" /> Anonymous submissions</label>
            <i
                class="far fa-info-circle"
                v-tooltip="'If enabled, the filler\'s name and email won\'t be collected when the form is submitted'"
            ></i>
        </div>
        <div v-if="signatures.length">
            <div class="toggle" @click="showRequiredSignatures = !showRequiredSignatures">
                <i class="far fa-chevron-down" :class="{ active: showRequiredSignatures }"></i> Required Signatures
                <i
                    class="far fa-info-circle"
                    v-tooltip="'Choose which signatures will be required before submission'"
                ></i>
            </div>

            <collapse-section :show="showRequiredSignatures">
                <div v-for="signature in signatures" :key="signature.id" class="signature">
                    <label>
                        <input
                            type="checkbox"
                            :checked="options.requiredSignatures.includes(signature.id)"
                            @change="toggleRequiredSignature(signature.id)"
                        />
                        {{ signature.name }}
                    </label>
                </div>
            </collapse-section>
        </div>
        <form-input
            v-if="pkg.is_public"
            v-model="options.submitRedirect"
            :trim="true"
            label="Redirect to a URL after submission"
            padding-top="1.4em"
            ref="submitRedirect"
        />
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    const isEqual = require('lodash/isEqual');
    const cloneDeep = require('lodash/cloneDeep');
    import isURL from 'validator/es/lib/isURL';

    export default {
        name: 'SendOptions',
        props: {
            autoSave: {
                type: Boolean,
                default: true,
            },
            contactId: {
                type: String,
            },
        },
        data() {
            return {
                options: {
                    enablePin: false,
                    pin: '',
                    pinHint: '',
                    disablePrint: false,
                    requiredSignatures: [],
                    submitRedirect: '',
                    anonymous: false,
                },
                initial: null,
                hasContactPin: false,
                showRequiredSignatures: false,
            };
        },
        computed: {
            ...mapGetters({
                pkg: 'package/pkg',
                pinProtection: 'package/pinProtection',
                contactById: 'package/contactById',
                org: 'org/org',
                linkData: 'package/linkData',
                featuresByType: 'form/featuresByType',
                forms: 'form/forms',
                fieldData: 'filler/fieldData',
            }),
            hasChanges() {
                for (let key in this.options) {
                    if (!isEqual(this.options[key], this.initial[key])) return true;
                }
                return false;
            },
            contact() {
                if (!this.contactId) return {};
                return this.contactById(this.contactId);
            },
            signatures() { // all signatures in this package
                return this.featuresByType('signature');
            },
            linkDefinedRequiredSignatureIds() {
                return this.linkData ? this.linkData.required_signatures : null;
            }
        },
        methods: {
            requireSignatureIfLinkedToContact() {
                // If there is a signature linked to a contact search and we're sending to the contact selected in the search, mark
                // signature required. We can't just do this on the initial load or a change won't be registered - this needs to save.

                // required signatures have already been saved, don't override
                if(this.linkDefinedRequiredSignatureIds) return;

                // no signatures in pkg, or not sending to a contact
                if (!this.signatures.length || !this.contactId) return;

                // find all contact search -> signature linkages
                for (let s of this.signatures) {
                    // if all featureLinks props are not null and equal each other, sig is linked to a contactSearch
                    let linkedContactSearchId =
                        s.featureLinks.email &&
                        s.featureLinks.email === s.featureLinks.fname &&
                        s.featureLinks.email === s.featureLinks.lname
                            ? s.featureLinks.email
                            : '';

                    if (linkedContactSearchId) {
                        // find which contact is selected in the linked contact search
                        for (let form of this.forms) {
                            let linkedContactSearchData = this.fieldData(form.id, linkedContactSearchId);
                            if (Object.keys(linkedContactSearchData).length) {
                                if (linkedContactSearchData.selectedDataElementDataId === this.contactId) {
                                    // sending package to the selected contact, mark signature as required if not already
                                    if (!this.options.requiredSignatures.includes(s.id)) this.toggleRequiredSignature(s.id);
                                }

                                break;
                            }
                        }
                    }
                }
            },
            toggleRequiredSignature(id) {
                let index = this.options.requiredSignatures.indexOf(id);
                if (index !== -1) {
                    this.options.requiredSignatures.splice(index, 1);
                } else {
                    this.options.requiredSignatures.push(id);
                    this.options.requiredSignatures = this.options.requiredSignatures.sort(); // TODO
                }
            },
            validate() {
                if (this.options.enablePin && this.options.pin.length < 4) {
                    this.$store.commit('tooltip/set', {
                        message: `PIN must be at least 4 characters long`,
                        trigger: this.$refs.pin.$el.querySelector('input'),
                    });
                    return false;
                }
                if (
                    this.options.submitRedirect &&
                    !isURL(this.options.submitRedirect, {
                        protocols: ['http', 'https'],
                        require_protocol: false,
                        validate_length: false,
                    })
                ) {
                    this.$store.commit('tooltip/set', {
                        message: `Invalid redirection URL`,
                        trigger: this.$refs.submitRedirect.$el.querySelector('input'),
                    });
                    return false;
                }
                return true;
            },
            async save() {
                if (!this.hasChanges || !this.validate()) return;

                let updates = {
                    pin: this.options.enablePin ? this.options.pin : '',
                    pin_hint: this.options.enablePin ? this.options.pinHint : '',
                    disable_print: this.options.disablePrint,
                    submit_redirect: this.options.submitRedirect,
                    required_signatures: this.signatures.length ? this.options.requiredSignatures : null,
                    anonymous: this.options.anonymous,
                };

                await this.$api.put(`packages/options/${this.pkg.id}`, updates);
                this.$store.commit('package/linkData', { ...this.linkData, ...updates });
                this.initial = cloneDeep(this.options);
            },
        },
        async mounted() {
            if (this.pinProtection) {
                this.hasContactPin = this.pinProtection.hasContactPin;
                this.options.enablePin = true;
                this.options.pin = this.pinProtection.pin;
                this.options.pinHint = this.pinProtection.pinHint;
            } else {
                if (this.pkg.is_public) {
                    this.hasContactPin = false;
                    this.options.enablePin = false;
                } else {
                    this.hasContactPin = this.contact.protectionEnabled;
                    this.options.enablePin = this.hasContactPin ? false : this.org.settings.requirePin;
                }
                this.options.pin = '';
                this.options.pinHint = '';
            }

            if (this.linkData) {
                this.options.disablePrint = this.linkData.disable_print;
                this.options.submitRedirect = this.linkData.submit_redirect || '';
                this.options.anonymous = this.linkData.anonymous;
            } else {
                this.options.disablePrint = false;
                this.options.submitRedirect = '';
                this.options.anonymous = false;
            }

            if (this.signatures.length) {
                if (this.linkDefinedRequiredSignatureIds) {
                    // use saved required signatures from link
                    this.options.requiredSignatures = this.linkDefinedRequiredSignatureIds.slice();
                } else {
                    // use form default required settings
                    this.options.requiredSignatures = this.signatures
                        .filter(
                            (s) =>
                                s.required === true || (Array.isArray(s.required) && s.required.includes('before_submit'))
                        )
                        .map((s) => s.id)
                        .sort();
                }
            }

            this.initial = cloneDeep(this.options);

            // Override defaults after initial values have been set so we can detect that save is required
            if (!this.linkData) {
                this.options.disablePrint = this.org.settings.disableFillerPrint;
            }

            this.requireSignatureIfLinkedToContact();
        },
        beforeDestroy() {
            if (this.autoSave) this.save();
        },
    };
</script>

<style lang="scss" scoped>
    .send-options {
        > *:not(:first-child) {
            margin-top: 0.5em;
        }

        .fa-lock {
            margin-right: 0.15rem;
        }

        .fa-info-circle {
            color: $color-label;
            margin-left: 0.25em;
        }
    }

    label,
    ::v-deep .label {
        font-weight: normal;
        font-size: 1em;
        user-select: none;
    }

    input[type='checkbox'] {
        margin: 0 0.3em 0 0;
        position: relative;
        top: -2px;
    }

    .toggle {
        user-select: none;
        cursor: pointer;
        margin-top: 0.5em;
        color: $color-label;

        i {
            color: $color-label;
            width: 1rem;
            margin-right: 0.3em;
            transition: transform 0.2s;
            text-align: center;

            &.active {
                transform: rotate(180deg);
            }
        }
    }

    .signature {
        padding: 0.5em 0.25em;
    }
</style>